// Theme Core Utilities
// Shared utilities and helpers used across different sections

/**
 * Debounce function to limit the rate of function calls
 * @param {Function} func - The function to debounce
 * @param {number} delay - The delay in milliseconds
 */
function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

/**
 * Throttle function to limit the rate of function calls
 * @param {Function} func - The function to throttle
 * @param {number} limit - The limit in milliseconds
 */
function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
}

/**
 * Check if element is in viewport
 * @param {Element} element - The element to check
 */
function isInViewport(element) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Get URL parameter value
 * @param {string} name - Parameter name
 */
function getURLParameter(name) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
}

/**
 * Set URL parameter
 * @param {string} name - Parameter name
 * @param {string} value - Parameter value
 */
function setURLParameter(name, value) {
  const url = new URL(window.location);
  url.searchParams.set(name, value);
  window.history.pushState({}, '', url);
}

/**
 * Remove URL parameter
 * @param {string} name - Parameter name
 */
function removeURLParameter(name) {
  const url = new URL(window.location);
  url.searchParams.delete(name);
  window.history.pushState({}, '', url);
}

/**
 * Format price using Shopify's money format
 * @param {number} cents - Price in cents
 */
function formatMoney(cents) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD'
  }).format(cents / 100);
}

/**
 * Show loading state
 * @param {Element} element - Element to show loading on
 */
function showLoading(element) {
  element.style.opacity = '0.5';
  element.style.pointerEvents = 'none';
}

/**
 * Hide loading state
 * @param {Element} element - Element to hide loading from
 */
function hideLoading(element) {
  element.style.opacity = '1';
  element.style.pointerEvents = 'auto';
}

/**
 * Toggle class on element
 * @param {Element} element - Element to toggle class on
 * @param {string} className - Class name to toggle
 */
function toggleClass(element, className) {
  if (element.classList.contains(className)) {
    element.classList.remove(className);
  } else {
    element.classList.add(className);
  }
}

/**
 * Add event listener with cleanup
 * @param {Element} element - Element to add listener to
 * @param {string} event - Event type
 * @param {Function} handler - Event handler
 */
function addListener(element, event, handler) {
  if (element) {
    element.addEventListener(event, handler);
  }
}

/**
 * Remove event listener
 * @param {Element} element - Element to remove listener from
 * @param {string} event - Event type
 * @param {Function} handler - Event handler
 */
function removeListener(element, event, handler) {
  if (element) {
    element.removeEventListener(event, handler);
  }
}

/**
 * Check if device is mobile
 */
function isMobile() {
  return window.innerWidth <= 768;
}

/**
 * Check if device is tablet
 */
function isTablet() {
  return window.innerWidth > 768 && window.innerWidth <= 1024;
}

/**
 * Check if device is desktop
 */
function isDesktop() {
  return window.innerWidth > 1024;
}

// Export functions for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    debounce,
    throttle,
    isInViewport,
    getURLParameter,
    setURLParameter,
    removeURLParameter,
    formatMoney,
    showLoading,
    hideLoading,
    toggleClass,
    addListener,
    removeListener,
    isMobile,
    isTablet,
    isDesktop
  };
}